/* ═══════════════════════════════════════════════════════════════
   FilterBuilder — the table's filter modal. Two panes: a searchable
   field rail on the left, the rule list on the right, a live match
   count in the footer.

   Edits a DRAFT copy of the filter state; nothing reaches the table
   until "Apply filters". Cancel / Esc / the close X discard.

   Group rules (round 2) already render read-only here so a state that
   carries them survives an edit round-trip untouched.
   ═══════════════════════════════════════════════════════════════ */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Search,
  Type as TypeIcon,
  List as ListIcon,
  Hash,
  Calendar,
  Plus,
  X,
  Layers,
  type LucideIcon,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Icon as KitIcon } from './ui/Icon';
import { SearchableSelect } from './SearchableSelect';
import { rem } from '../lib/rem';
import {
  EMPTY_FILTER_STATE,
  OPS_BY_KIND,
  QUICK_DATE_PRESETS,
  activeRuleCount,
  defaultOpFor,
  defaultValueFor,
  groupSummaryText,
  isFilterGroup,
  opLabel,
  pruneInactive,
  type FilterFieldKind,
  type FilterOp,
  type FilterRule,
  type MatchMode,
  type TableFilterState,
} from './filterModel';

export interface FilterField {
  key: string;
  label: string;
  kind: FilterFieldKind;
  options?: { value: string; label: string }[];
}

export interface FilterBuilderProps {
  open: boolean;
  onClose: () => void;
  fields: FilterField[];
  value: TableFilterState;
  onApply: (next: TableFilterState) => void;
  /** Rows the table currently holds (before filtering) — the "of M" half. */
  totalRows: number;
  /** Live count of rows the draft would keep. */
  countMatches: (state: TableFilterState) => number;
  /** Hands the applied draft to the host's existing saved-view flow. */
  onSaveAsView?: (state: TableFilterState) => void;
}

// ─── Shared styles (tokens only) ───

const inputStyle: React.CSSProperties = {
  width: '100%',
  minHeight: 'var(--space-6)',
  padding: `${rem(6)} var(--space-2)`,
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--bd)',
  background: 'var(--ib)',
  color: 'var(--tx)',
  fontSize: 'var(--font-size-base)',
  boxSizing: 'border-box',
  // No `outline: none` here on purpose — every control keeps a visible focus
  // ring (DESIGN §7).
};

const KIND_ICON: Record<FilterFieldKind, LucideIcon> = {
  text: TypeIcon,
  multiselect: ListIcon,
  number: Hash,
  date: Calendar,
};

const KIND_TITLE: Record<FilterFieldKind, string> = {
  text: 'Text field',
  multiselect: 'List field',
  number: 'Number field',
  date: 'Date field',
};

// ─── Value helpers ───

function asStr(v: unknown): string {
  return v == null ? '' : String(v);
}

function dateBounds(rule: FilterRule): { min?: string; max?: string } {
  if (rule.op === 'after') return { min: asStr(rule.value) || undefined };
  if (rule.op === 'before') return { max: asStr(rule.value) || undefined };
  if (rule.op === 'betweenDates') {
    const v = (rule.value || {}) as { min?: unknown; max?: unknown };
    return { min: asStr(v.min) || undefined, max: asStr(v.max) || undefined };
  }
  return {};
}

/** "After" and "Before" are one control — which op the rule ends up with
 *  falls out of which side the user filled in. */
function withDateBound(rule: FilterRule, side: 'min' | 'max', raw: string): FilterRule {
  const cur = dateBounds(rule);
  const next = { ...cur, [side]: raw || undefined };
  if (next.min && next.max) return { ...rule, op: 'betweenDates', value: next };
  if (next.min) return { ...rule, op: 'after', value: next.min };
  if (next.max) return { ...rule, op: 'before', value: next.max };
  return { ...rule, op: 'betweenDates', value: { min: undefined, max: undefined } };
}

const TEXT_OPS: FilterOp[] = ['contains', 'notContains', 'is', 'isNot'];
const NUM_OPS: FilterOp[] = ['eq', 'neq', 'gt', 'lt'];
const DATE_BOUND_OPS: FilterOp[] = ['after', 'before', 'betweenDates'];

/** Switching operator keeps whatever the old value can still mean. */
function withOp(rule: FilterRule, op: FilterOp): FilterRule {
  if (op === rule.op) return rule;
  const old = rule.value;
  if (TEXT_OPS.includes(op) && TEXT_OPS.includes(rule.op)) return { ...rule, op, value: old };
  if (
    (op === 'anyOf' || op === 'noneOf') &&
    (rule.op === 'anyOf' || rule.op === 'noneOf') &&
    Array.isArray(old)
  ) {
    return { ...rule, op, value: old };
  }
  if (NUM_OPS.includes(op) && NUM_OPS.includes(rule.op)) return { ...rule, op, value: old };
  if (NUM_OPS.includes(op) && rule.op === 'between') {
    const v = (old || {}) as { min?: unknown };
    return { ...rule, op, value: v.min };
  }
  if (op === 'between' && NUM_OPS.includes(rule.op)) {
    return { ...rule, op, value: { min: old, max: undefined } };
  }
  if (DATE_BOUND_OPS.includes(op) && DATE_BOUND_OPS.includes(rule.op)) {
    const b = dateBounds(rule);
    if (op === 'after') return { ...rule, op, value: b.min };
    if (op === 'before') return { ...rule, op, value: b.max };
    return { ...rule, op, value: { min: b.min, max: b.max } };
  }
  return { ...rule, op, value: defaultValueFor(op) };
}

// ─── Component ───

export function FilterBuilder({
  open,
  onClose,
  fields,
  value,
  onApply,
  totalRows,
  countMatches,
  onSaveAsView,
}: FilterBuilderProps) {
  const [draft, setDraft] = useState<TableFilterState>(value || EMPTY_FILTER_STATE);
  const [search, setSearch] = useState('');
  const [focusIdx, setFocusIdx] = useState<number | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Fresh draft every time the modal opens — never carry a discarded edit.
  useEffect(() => {
    if (open) {
      setDraft(value || EMPTY_FILTER_STATE);
      setSearch('');
      setFocusIdx(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fieldByKey = useMemo(() => {
    const m = new Map<string, FilterField>();
    for (const f of fields) m.set(f.key, f);
    return m;
  }, [fields]);

  const labelOf = useCallback((key: string) => fieldByKey.get(key)?.label || key, [fieldByKey]);

  const kindOf = useCallback(
    (key: string): FilterFieldKind => fieldByKey.get(key)?.kind || 'text',
    [fieldByKey]
  );

  const activeFieldKeys = useMemo(() => {
    const s = new Set<string>();
    for (const r of draft.rules) {
      if (isFilterGroup(r)) r.rules.forEach((x) => s.add(x.field));
      else s.add(r.field);
    }
    return s;
  }, [draft]);

  const visibleFields = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? fields.filter((f) => f.label.toLowerCase().includes(q)) : fields;
  }, [fields, search]);

  const matchCount = useMemo(() => countMatches(draft), [draft, countMatches]);
  const activeCount = activeRuleCount(draft);

  // ── Draft mutations ──

  function setRule(idx: number, next: FilterRule) {
    setDraft((d) => ({ ...d, rules: d.rules.map((r, i) => (i === idx ? next : r)) }));
  }

  function removeRule(idx: number) {
    setDraft((d) => ({ ...d, rules: d.rules.filter((_, i) => i !== idx) }));
  }

  function addRule(fieldKey?: string) {
    const f = fieldKey ? fieldByKey.get(fieldKey) : fields[0];
    if (!f) return;
    const op = defaultOpFor(f.kind);
    setDraft((d) => ({
      ...d,
      rules: [...d.rules, { field: f.key, op, value: defaultValueFor(op) }],
    }));
    setFocusIdx(draft.rules.length);
    setTimeout(() => {
      listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
    }, 0);
  }

  function changeField(idx: number, rule: FilterRule, nextKey: string) {
    if (!nextKey || nextKey === rule.field) return;
    const prevKind = kindOf(rule.field);
    const nextKind = kindOf(nextKey);
    if (prevKind === nextKind) {
      setRule(idx, { ...rule, field: nextKey });
      return;
    }
    const op = defaultOpFor(nextKind);
    setRule(idx, { field: nextKey, op, value: defaultValueFor(op) });
  }

  function commit() {
    onApply(pruneInactive(draft));
    onClose();
  }

  // ── Value editor ──

  function renderValueEditor(rule: FilterRule, idx: number) {
    const kind = kindOf(rule.field);
    const autoFocus = focusIdx === idx;

    if (
      rule.op === 'isEmpty' ||
      rule.op === 'isNotEmpty' ||
      rule.op === 'today' ||
      rule.op === 'next7' ||
      rule.op === 'next30' ||
      rule.op === 'overdue' ||
      rule.op === 'thisMonth'
    ) {
      return (
        <span
          style={{
            fontSize: 'calc(var(--font-size-base) * 12 / 13)',
            color: 'var(--tm)',
            alignSelf: 'center',
          }}
        >
          No value needed
        </span>
      );
    }

    if (kind === 'multiselect') {
      const f = fieldByKey.get(rule.field);
      return (
        <SearchableSelect
          multiple
          value=""
          values={Array.isArray(rule.value) ? (rule.value as string[]) : []}
          onChange={() => {}}
          onChangeMulti={(vals) => setRule(idx, { ...rule, value: vals })}
          options={f?.options || []}
          placeholder="Pick values…"
          searchPlaceholder="Search values…"
          ariaLabel={`${labelOf(rule.field)} values`}
          style={{ minHeight: 'var(--space-6)', fontSize: 'var(--font-size-base)' }}
        />
      );
    }

    if (kind === 'number') {
      if (rule.op === 'between') {
        const v = (rule.value || {}) as { min?: unknown; max?: unknown };
        return (
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <input
              type="number"
              autoFocus={autoFocus}
              value={asStr(v.min)}
              onChange={(e) =>
                setRule(idx, {
                  ...rule,
                  value: {
                    min: e.target.value === '' ? undefined : Number(e.target.value),
                    max: v.max,
                  },
                })
              }
              placeholder="Min"
              aria-label={`${labelOf(rule.field)} minimum`}
              style={inputStyle}
            />
            <input
              type="number"
              value={asStr(v.max)}
              onChange={(e) =>
                setRule(idx, {
                  ...rule,
                  value: {
                    min: v.min,
                    max: e.target.value === '' ? undefined : Number(e.target.value),
                  },
                })
              }
              placeholder="Max"
              aria-label={`${labelOf(rule.field)} maximum`}
              style={inputStyle}
            />
          </div>
        );
      }
      return (
        <input
          type="number"
          autoFocus={autoFocus}
          value={asStr(rule.value)}
          onChange={(e) =>
            setRule(idx, {
              ...rule,
              value: e.target.value === '' ? undefined : Number(e.target.value),
            })
          }
          placeholder="Value"
          aria-label={`${labelOf(rule.field)} value`}
          style={inputStyle}
        />
      );
    }

    if (kind === 'date') {
      if (rule.op === 'inNext') {
        const days = Number((rule.value as { days?: unknown })?.days) || 0;
        const preset = QUICK_DATE_PRESETS.find((p) => p.days === days);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: rem(6),
                minHeight: 'var(--space-6)',
                padding: `var(--space-1) ${rem(6)} var(--space-1) ${rem(10)}`,
                borderRadius: 'var(--radius-full)',
                fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                color: 'var(--tx)',
                background: 'color-mix(in srgb, var(--ac) 10%, transparent)',
                border: '1px solid color-mix(in srgb, var(--ac) 25%, transparent)',
              }}
            >
              {preset
                ? preset.label.charAt(0).toUpperCase() + preset.label.slice(1)
                : `${days} days`}
              <button
                type="button"
                onClick={() =>
                  setRule(idx, {
                    ...rule,
                    op: 'betweenDates',
                    value: { min: undefined, max: undefined },
                  })
                }
                aria-label="Use a date range instead"
                title="Use a date range instead"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: rem(20),
                  height: rem(20),
                  border: 'none',
                  background: 'none',
                  color: 'var(--tm)',
                  cursor: 'pointer',
                  borderRadius: 'var(--radius-full)',
                }}
              >
                <KitIcon icon={X} size="md" />
              </button>
            </span>
          </div>
        );
      }
      const b = dateBounds(rule);
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <label style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: rem(2) }}>
              <span
                style={{ fontSize: 'var(--font-size-xs)', color: 'var(--tm)', fontWeight: 600 }}
              >
                After
              </span>
              <input
                type="date"
                autoFocus={autoFocus}
                value={b.min || ''}
                onChange={(e) => setRule(idx, withDateBound(rule, 'min', e.target.value))}
                style={inputStyle}
              />
            </label>
            <label style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: rem(2) }}>
              <span
                style={{ fontSize: 'var(--font-size-xs)', color: 'var(--tm)', fontWeight: 600 }}
              >
                Before
              </span>
              <input
                type="date"
                value={b.max || ''}
                onChange={(e) => setRule(idx, withDateBound(rule, 'max', e.target.value))}
                style={inputStyle}
              />
            </label>
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            {QUICK_DATE_PRESETS.map((p) => (
              <button
                key={p.days}
                type="button"
                onClick={() => setRule(idx, { ...rule, op: 'inNext', value: { days: p.days } })}
                style={{
                  minHeight: 'var(--space-6)',
                  padding: 'var(--space-1) var(--space-3)',
                  borderRadius: 'var(--radius-full)',
                  border: '1px solid var(--bd)',
                  background: 'var(--sa)',
                  color: 'var(--tm)',
                  fontSize: 'var(--font-size-sm)',
                  cursor: 'pointer',
                }}
              >
                {p.label.charAt(0).toUpperCase() + p.label.slice(1)}
              </button>
            ))}
          </div>
        </div>
      );
    }

    return (
      <input
        type="text"
        autoFocus={autoFocus}
        value={asStr(rule.value)}
        onChange={(e) => setRule(idx, { ...rule, value: e.target.value })}
        placeholder="Value"
        aria-label={`${labelOf(rule.field)} value`}
        style={inputStyle}
      />
    );
  }

  // ── Render ──

  const matchBtn = (mode: MatchMode, label: string) => {
    const on = draft.match === mode;
    return (
      <button
        type="button"
        role="radio"
        aria-checked={on}
        onClick={() => setDraft((d) => ({ ...d, match: mode }))}
        style={{
          minHeight: rem(30),
          padding: `var(--space-1) ${rem(14)}`,
          border: 'none',
          background: on ? 'var(--sf)' : 'transparent',
          color: on ? 'var(--ac)' : 'var(--tm)',
          fontWeight: on ? 700 : 500,
          fontSize: 'calc(var(--font-size-base) * 12 / 13)',
          borderRadius: 'var(--radius-sm)',
          cursor: 'pointer',
          boxShadow: on ? 'var(--shadow-sm)' : 'none',
        }}
      >
        {label}
      </button>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-none p-0 gap-0"
        style={{
          width: rem(860),
          maxWidth: 'calc(100vw - var(--space-6))',
          maxHeight: `min(86vh, ${rem(720)})`,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        aria-describedby={undefined}
      >
        <DialogHeader
          style={{
            padding: 'var(--space-4) var(--space-5)',
            paddingRight: 'var(--space-7)',
            borderBottom: '1px solid var(--bd)',
            flexShrink: 0,
          }}
        >
          <DialogTitle>Filter builder</DialogTitle>
        </DialogHeader>

        <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
          {/* Left rail — every filterable field, searchable */}
          <div
            style={{
              width: rem(236),
              flexShrink: 0,
              background: 'var(--sa)',
              borderRight: '1px solid var(--bd)',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            <div style={{ padding: 'var(--space-3)', borderBottom: '1px solid var(--bd)' }}>
              <div style={{ position: 'relative' }}>
                <KitIcon
                  icon={Search}
                  size="md"
                  aria-hidden
                  style={{
                    position: 'absolute',
                    left: 'var(--space-2)',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--tm)',
                  }}
                />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Find a field…"
                  aria-label="Find a field"
                  style={{ ...inputStyle, paddingLeft: rem(26) }}
                />
              </div>
            </div>
            <div style={{ overflowY: 'auto', flex: 1, padding: 'var(--space-1) 0' }}>
              {visibleFields.length === 0 && (
                <div
                  style={{
                    padding: 'var(--space-4)',
                    fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                    color: 'var(--tm)',
                    textAlign: 'center',
                  }}
                >
                  No fields match that.
                </div>
              )}
              {visibleFields.map((f) => {
                const Icon = KIND_ICON[f.kind];
                const isActive = activeFieldKeys.has(f.key);
                return (
                  <button
                    key={f.key}
                    type="button"
                    data-filter-field={f.key}
                    onClick={() => addRule(f.key)}
                    title={`Add a ${KIND_TITLE[f.kind].toLowerCase()} rule for ${f.label}`}
                    style={{
                      width: '100%',
                      minHeight: 'var(--space-6)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-2)',
                      padding: `${rem(6)} var(--space-3)`,
                      border: 'none',
                      background: 'transparent',
                      color: 'var(--tx)',
                      fontSize: 'var(--font-size-base)',
                      textAlign: 'left',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--sf)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <span
                      title={KIND_TITLE[f.kind]}
                      style={{ display: 'flex', color: 'var(--tm)', flexShrink: 0 }}
                    >
                      <KitIcon icon={Icon} size="md" />
                    </span>
                    <span
                      style={{
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {f.label}
                    </span>
                    {isActive && (
                      <span
                        aria-label="Has active rules"
                        style={{
                          width: rem(6),
                          height: rem(6),
                          borderRadius: 'var(--radius-full)',
                          background: 'var(--ac)',
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right pane — the rules */}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                padding: 'var(--space-3) var(--space-4)',
                borderBottom: '1px solid var(--bd)',
                flexShrink: 0,
                flexWrap: 'wrap',
              }}
            >
              <span style={{ fontSize: 'var(--font-size-base)', color: 'var(--tx)' }}>
                Show rows that match
              </span>
              <span
                role="radiogroup"
                aria-label="Match all or any rule"
                style={{
                  display: 'inline-flex',
                  gap: rem(2),
                  padding: rem(2),
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--sa)',
                  border: '1px solid var(--bd)',
                }}
              >
                {matchBtn('all', 'All')}
                {matchBtn('any', 'Any')}
              </span>
              <span style={{ fontSize: 'var(--font-size-base)', color: 'var(--tx)' }}>
                of these rules:
              </span>
            </div>

            <div
              ref={listRef}
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                padding: 'var(--space-3) var(--space-4)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-2)',
              }}
            >
              {draft.rules.length === 0 && (
                <div
                  style={{
                    padding: 'var(--space-6) var(--space-4)',
                    textAlign: 'center',
                    color: 'var(--tm)',
                    fontSize: 'var(--font-size-base)',
                  }}
                >
                  No rules yet — pick a field on the left to start narrowing this table.
                </div>
              )}

              {draft.rules.map((node, idx) => {
                // Groups are round-2 territory: shown, kept, never edited here.
                if (isFilterGroup(node)) {
                  return (
                    <div
                      key={`g${idx}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                        padding: 'var(--space-2) var(--space-3)',
                        border: '1px dashed var(--bd)',
                        borderRadius: 'var(--radius-md)',
                        background: 'var(--sa)',
                        fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                        color: 'var(--tx)',
                      }}
                    >
                      <KitIcon
                        icon={Layers}
                        em={1.0833}
                        aria-hidden
                        style={{ color: 'var(--tm)', flexShrink: 0 }}
                      />
                      <span style={{ flex: 1, minWidth: 0 }}>
                        {groupSummaryText(node, labelOf) || 'Empty group'}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeRule(idx)}
                        aria-label="Remove this group"
                        title="Remove this group"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 'var(--space-6)',
                          height: 'var(--space-6)',
                          border: 'none',
                          background: 'none',
                          color: 'var(--tm)',
                          cursor: 'pointer',
                          borderRadius: 'var(--radius-sm)',
                        }}
                      >
                        <KitIcon icon={X} em={1.1667} />
                      </button>
                    </div>
                  );
                }

                const rule = node;
                const kind = kindOf(rule.field);
                return (
                  <div
                    key={`r${idx}`}
                    data-filter-rule={rule.field}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: `${rem(190)} ${rem(150)} 1fr var(--space-6)`,
                      gap: 'var(--space-2)',
                      alignItems: 'start',
                    }}
                  >
                    <SearchableSelect
                      value={rule.field}
                      options={fields.map((f) => ({ value: f.key, label: f.label }))}
                      onChange={(v) => changeField(idx, rule, v)}
                      searchPlaceholder="Search fields…"
                      clearable={false}
                      ariaLabel="Field"
                      style={{ minHeight: 'var(--space-6)', fontSize: 'var(--font-size-base)' }}
                    />
                    <SearchableSelect
                      value={rule.op}
                      options={OPS_BY_KIND[kind].map((o) => ({ value: o, label: opLabel(o) }))}
                      onChange={(v) => v && setRule(idx, withOp(rule, v as FilterOp))}
                      searchPlaceholder="Search…"
                      clearable={false}
                      ariaLabel="Operator"
                      style={{ minHeight: 'var(--space-6)', fontSize: 'var(--font-size-base)' }}
                    />
                    <div style={{ minWidth: 0 }}>{renderValueEditor(rule, idx)}</div>
                    <button
                      type="button"
                      onClick={() => removeRule(idx)}
                      aria-label={`Remove the ${labelOf(rule.field)} rule`}
                      title="Remove rule"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 'var(--space-6)',
                        height: 'var(--space-6)',
                        border: 'none',
                        background: 'none',
                        color: 'var(--tm)',
                        cursor: 'pointer',
                        borderRadius: 'var(--radius-sm)',
                      }}
                    >
                      <KitIcon icon={X} em={1.0769} />
                    </button>
                  </div>
                );
              })}

              {fields.length > 0 && (
                <div>
                  <Button variant="ghost" size="sm" onClick={() => addRule()}>
                    <KitIcon icon={Plus} em={1.3} />
                    Add rule
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer — live count left, actions right */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            padding: 'var(--space-3) var(--space-4)',
            borderTop: '1px solid var(--bd)',
            background: 'var(--sa)',
            flexShrink: 0,
          }}
        >
          <span
            aria-live="polite"
            style={{ fontSize: 'var(--font-size-base)', color: 'var(--tm)' }}
          >
            <strong style={{ color: 'var(--tx)', fontWeight: 700 }}>{matchCount}</strong> of{' '}
            {totalRows} rows match
          </span>
          {activeCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDraft({ version: 2, match: 'all', rules: [] })}
            >
              Clear all
            </Button>
          )}
          <div style={{ flex: 1 }} />
          {onSaveAsView && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                onSaveAsView(pruneInactive(draft));
                onClose();
              }}
            >
              Save as view…
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" onClick={commit}>
            Apply filters
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default FilterBuilder;
